'use client';

import type { ComponentProps } from 'react';
import type { ButtonProps } from '@heroui/react';

import React from 'react';
import { useControlledState } from '@react-stately/utils';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { cn } from '@heroui/react';
// Локальная функция для получения цветов темы
const getThemeColors = () => {
  // Определяем тип приложения по URL или другим признакам
  const isPartner = window.location.pathname.includes('/partner');

  if (isPartner) {
    return {
      primary: { background: '#006FEE', text: '#FFFFFF' },
      secondary: { background: '#E6F1FE', text: '#006FEE' },
    };
  } else {
    return {
      primary: { background: '#9675CE', text: '#FFFFFF' },
      secondary: { background: '#F1E8FF', text: '#9675CE' },
    };
  }
};

export type HorizontalStepProps = {
  title?: React.ReactNode;
  className?: string;
};

export interface HorizontalStepsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * An array of steps.
   *
   * @default []
   */
  steps?: HorizontalStepProps[];
  /**
   * The color of the steps.
   *
   * @default "primary"
   */
  color?: ButtonProps['color'];
  /**
   * The current step index.
   */
  currentStep?: number;
  /**
   * The default step index.
   *
   * @default 0
   */
  defaultStep?: number;
  /**
   * Whether to hide the progress bars.
   *
   * @default false
   */
  hideProgressBars?: boolean;
  /**
   * The custom class for the steps wrapper.
   */
  className?: string;
  /**
   * The custom class for the step.
   */
  stepClassName?: string;
  /**
   * Callback function when the step index changes.
   */
  onStepChange?: (stepIndex: number) => void;
}

function CheckIcon(props: ComponentProps<'svg'>) {
  return (
    <svg {...props} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <m.path
        animate={{ pathLength: 1 }}
        d="M5 13l4 4L19 7"
        initial={{ pathLength: 0 }}
        strokeLinecap="round"
        strokeLinejoin="round"
        transition={{
          delay: 0.2,
          type: 'tween',
          ease: 'easeOut',
          duration: 0.3,
        }}
      />
    </svg>
  );
}

const HorizontalSteps = React.forwardRef<HTMLDivElement, HorizontalStepsProps>(
  (
    {
      color = 'primary',
      steps = [],
      defaultStep = 0,
      onStepChange,
      currentStep: currentStepProp,
      hideProgressBars = false,
      stepClassName,
      className,
      ...props
    },
    ref
  ) => {
    const [currentStep, setCurrentStep] = useControlledState(
      currentStepProp,
      defaultStep,
      onStepChange
    );

    const themeColors = React.useMemo(() => {
      return getThemeColors();
    }, []);

    const getStepColors = React.useMemo(() => {
      switch (color) {
        case 'primary':
          return {
            activeColor: themeColors.primary.background,
            activeTextColor: themeColors.primary.text,
            completeColor: themeColors.primary.background,
            completeTextColor: themeColors.primary.text,
          };
        case 'secondary':
          return {
            activeColor: themeColors.secondary.background,
            activeTextColor: themeColors.secondary.text,
            completeColor: themeColors.secondary.background,
            completeTextColor: themeColors.secondary.text,
          };
        case 'success':
          return {
            activeColor: 'var(--heroui-success)',
            activeTextColor: 'var(--heroui-success-foreground)',
            completeColor: 'var(--heroui-success)',
            completeTextColor: 'var(--heroui-success-foreground)',
          };
        case 'warning':
          return {
            activeColor: 'var(--heroui-warning)',
            activeTextColor: 'var(--heroui-warning-foreground)',
            completeColor: 'var(--heroui-warning)',
            completeTextColor: 'var(--heroui-warning-foreground)',
          };
        case 'danger':
          return {
            activeColor: 'var(--heroui-danger)',
            activeTextColor: 'var(--heroui-danger-foreground)',
            completeColor: 'var(--heroui-danger)',
            completeTextColor: 'var(--heroui-danger-foreground)',
          };
        default:
          return {
            activeColor: themeColors.primary.background,
            activeTextColor: themeColors.primary.text,
            completeColor: themeColors.primary.background,
            completeTextColor: themeColors.primary.text,
          };
      }
    }, [color, themeColors]);

    return (
      <LazyMotion features={domAnimation}>
        <div ref={ref} className={cn('flex items-start', className)} {...props}>
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const isInactive = index > currentStep;

            return (
              <React.Fragment key={index}>
                <div className={cn('flex flex-col items-start gap-1', stepClassName)}>
                  <div className="flex items-center">
                    <div
                      className={cn(
                        'flex pr-[.0313rem] pb-0 h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-300 flex-shrink-0'
                      )}
                      style={{
                        borderColor: isActive
                          ? getStepColors.activeColor
                          : isCompleted
                            ? getStepColors.completeColor
                            : '#D4D4D8',
                        color: isActive
                          ? getStepColors.activeColor
                          : isCompleted
                            ? getStepColors.completeTextColor
                            : '#888C8E',
                        backgroundColor: isCompleted ? getStepColors.completeColor : 'transparent',
                      }}
                    >
                      {isCompleted ? (
                        <CheckIcon className="h-6 w-6" />
                      ) : (
                        <span className="text-lg text-center leading-[1.125rem] font-medium">
                          {index + 1}
                        </span>
                      )}
                    </div>
                    {index < steps.length - 1 && !hideProgressBars && (
                      <div
                        className="h-0.5 w-[12.5rem] mx-[.9375rem] transition-all duration-300"
                        style={{
                          backgroundColor: isCompleted ? getStepColors.completeColor : '#D4D4D8',
                        }}
                      />
                    )}
                  </div>
                  {step.title && (
                    <div className="flex flex-col items-start text-left">{step.title}</div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </LazyMotion>
    );
  }
);

HorizontalSteps.displayName = 'HorizontalSteps';

export { HorizontalSteps };
